import type {
  ComponentAttributeRead,
  CoreAttributeRead,
  ProductAttributesRead,
  ProductTypeRead,
  QcFindingRead,
} from '../types/attribution.types';
import type {
  LatestReviewRead,
  QcCheck,
  ReviewDecision,
  ReviewEventCreate,
  ReviewSubject,
} from '../types/review.types';
import { QC_CHECKS } from '../types/review.types';

import { QC_CHECK_GLOSSARY, QC_SEVERITY_GLOSSARY } from './glossary';
import type { StatusTone } from './run-status';
import { humanizeEnum } from './run-status';

/*
 * The Review page's model: which items on a product a reviewer judges, how a
 * server decision maps onto each, and when a product counts as reviewed.
 *
 * A product is REVIEWED when its detection facts (and listing shape), every
 * found detail (and every detail of a set's pieces) has a server verdict, and
 * every QC finding is confirmed or dismissed. Nothing is stored per product.
 */

/** Below this an answer is flagged for a closer look. */
export const LOW_CONFIDENCE = 0.7;

/** A reviewer's ✓ / ✗ on one answer, as the UI shows it. */
export type ItemVerdict = 'correct' | 'wrong';

/** A reviewer's call on one QC finding, as the UI shows it. */
export type FindingVerdict = 'real' | 'dismissed';

/** What the reviewer judges. `kind` decides where it renders. */
export interface ReviewItem {
  /** Identity the service groups decisions on — see `reviewItemKey`. */
  key: string;
  kind: 'fact' | 'shape' | 'detail' | 'piece' | 'finding';
  subject: ReviewSubject;
  component: string;
  attributeName: string;
  modelValue: string;
  /** Only on findings; `''` everywhere else. */
  checkName: QcCheck | '';
  promptVersion: string;
  extractorVersion: string;
}

/**
 * The service keeps the latest decision per (subject, component, attributeName,
 * modelValue, checkName); joining the same five is how a decision finds its item.
 */
export function reviewItemKey(item: {
  subject: string;
  component?: string;
  attributeName?: string;
  modelValue?: string;
  checkName?: string | null;
}): string {
  return [
    item.subject,
    item.component ?? '',
    item.attributeName ?? '',
    item.modelValue ?? '',
    item.checkName ?? '',
  ].join('\u001f');
}

/** Builds an item, deriving its key from the identity fields. */
function makeItem(fields: Omit<ReviewItem, 'key'>): ReviewItem {
  return { ...fields, key: reviewItemKey(fields) };
}

/** Narrows a lenient server string to the QC rule vocabulary (the POST 422s on anything else). */
export function asQcCheck(value: string): QcCheck | '' {
  return (QC_CHECKS as readonly string[]).includes(value) ? (value as QcCheck) : '';
}

/** The detection facts a reviewer confirms, with the label each is shown under. */
export const FACT_LABEL: Record<string, string> = {
  googleProductCategory: 'What it is',
  productType: 'Product group',
  personType: "Who it's for",
};

/** The three detection facts. `modelValue` is the model's raw value, so it survives label changes. */
export function detectionFactItems(detection: ProductTypeRead): ReviewItem[] {
  return (['googleProductCategory', 'productType', 'personType'] as const).map((name) =>
    makeItem({
      kind: 'fact',
      subject: 'PRODUCT_TYPE',
      component: '',
      attributeName: name,
      modelValue: detection[name],
      checkName: '',
      promptVersion: detection.promptVersion,
      extractorVersion: '',
    }),
  );
}

/** The listing shape (one item / pack / set) — null on a deployment that does not report it. */
export function listingShapeItem(detection: ProductTypeRead): ReviewItem | null {
  if (!detection.setKind) return null;
  return makeItem({
    kind: 'shape',
    subject: 'LISTING_SHAPE',
    component: '',
    attributeName: '',
    modelValue: `${detection.setKind}:${String(detection.packQuantity ?? 1)}`,
    checkName: '',
    promptVersion: detection.promptVersion,
    extractorVersion: '',
  });
}

/** "One item" · "Pack of 4" · "Set of 3 pieces" — the listing shape in words. */
export function listingShapeLabel(detection: ProductTypeRead): string {
  const n = detection.packQuantity ?? 1;
  if (detection.setKind === 'MULTIPACK') return `Pack of ${String(n)}`;
  if (detection.setKind === 'SET') return `Set of ${String(n)} ${n === 1 ? 'piece' : 'pieces'}`;
  return 'One item';
}

/** True for a value the model gave (anything but "asked and not answered"). */
export function isFoundValue(grounding: string): boolean {
  return grounding !== 'NONE';
}

/** One judgeable item per found listing detail. */
export function detailItem(attribute: CoreAttributeRead): ReviewItem {
  return makeItem({
    kind: 'detail',
    subject: 'CORE_ATTRIBUTE',
    component: '',
    attributeName: attribute.attributeName,
    modelValue: attribute.attributeValue,
    checkName: '',
    promptVersion: attribute.promptVersion,
    extractorVersion: attribute.extractorVersion,
  });
}

/** One judgeable item per found detail of a set's piece. */
export function pieceItem(attribute: ComponentAttributeRead): ReviewItem {
  return makeItem({
    kind: 'piece',
    subject: 'COMPONENT_ATTRIBUTE',
    component: attribute.component,
    attributeName: attribute.attributeName,
    modelValue: attribute.attributeValue,
    checkName: '',
    promptVersion: attribute.promptVersion,
    extractorVersion: '',
  });
}

/** One QC finding as a judgeable item (Real issue / Not an issue). */
export function findingItem(finding: QcFindingRead): ReviewItem {
  const subject = (
    ['PRODUCT_TYPE', 'LISTING_SHAPE', 'CORE_ATTRIBUTE', 'COMPONENT_ATTRIBUTE'].includes(
      finding.subject,
    )
      ? finding.subject
      : 'CORE_ATTRIBUTE'
  ) as ReviewSubject;
  return makeItem({
    kind: 'finding',
    subject,
    component: finding.component,
    attributeName: finding.attributeName,
    modelValue: finding.attributeValue,
    checkName: asQcCheck(finding.checkName),
    promptVersion: finding.promptVersion,
    extractorVersion: '',
  });
}

/** The ADDED item for "add something we missed" — one per detail name (the latest stands). */
export function addedItem(attributeName: string, component = ''): ReviewItem {
  return makeItem({
    kind: 'detail',
    subject: component ? 'COMPONENT_ATTRIBUTE' : 'CORE_ATTRIBUTE',
    component,
    attributeName,
    modelValue: '',
    checkName: '',
    promptVersion: '',
    extractorVersion: '',
  });
}

/** Turns an item + decision into the POST body entry, leaving out empty optional fields. */
export function toReviewEvent(
  item: ReviewItem,
  verdict: ReviewDecision,
  context: { productId: string; runId: string },
  extra: { correctedValue?: string; note?: string } = {},
): ReviewEventCreate {
  return {
    productId: context.productId,
    subject: item.subject,
    verdict,
    ...(item.component && { component: item.component }),
    ...(item.attributeName && { attributeName: item.attributeName }),
    ...(item.modelValue && { modelValue: item.modelValue }),
    ...(item.checkName && { checkName: item.checkName }),
    ...(extra.correctedValue && { correctedValue: extra.correctedValue }),
    ...(extra.note && { note: extra.note }),
    ...(context.runId && { runId: context.runId }),
    ...(item.promptVersion && { promptVersion: item.promptVersion }),
    ...(item.extractorVersion && { extractorVersion: item.extractorVersion }),
  };
}

/** Latest decision per item key, leaving out WITHDRAWN (the item is unreviewed again). */
export function latestByKey(
  reviews: LatestReviewRead[] | undefined,
): Map<string, LatestReviewRead> {
  const map = new Map<string, LatestReviewRead>();
  for (const review of reviews ?? []) {
    if (review.verdict === 'WITHDRAWN') continue;
    map.set(reviewItemKey(review), review);
  }
  return map;
}

/** Server verdict → the ✓ / ✗ the UI shows (undefined while unjudged). */
export function itemVerdict(review: LatestReviewRead | undefined): ItemVerdict | undefined {
  if (review?.verdict === 'CORRECT') return 'correct';
  if (review?.verdict === 'WRONG') return 'wrong';
  return undefined;
}

/** Server verdict → Real issue / Not an issue on a finding. */
export function findingVerdict(review: LatestReviewRead | undefined): FindingVerdict | undefined {
  if (review?.verdict === 'FINDING_CONFIRMED') return 'real';
  if (review?.verdict === 'FINDING_DISMISSED') return 'dismissed';
  return undefined;
}

/** Everything the page needs to know about one product's review, derived from its product view. */
export interface ProductReviewModel {
  detection: ProductTypeRead;
  facts: ReviewItem[];
  shape: ReviewItem | null;
  details: { item: ReviewItem; attribute: CoreAttributeRead }[];
  pieces: { item: ReviewItem; attribute: ComponentAttributeRead }[];
  findings: { item: ReviewItem; finding: QcFindingRead }[];
  /** Latest non-withdrawn decision per item key. */
  latest: Map<string, LatestReviewRead>;
  /** Facts, shape, details and pieces nobody has judged yet. */
  unjudged: ReviewItem[];
  /** Findings nobody has confirmed or dismissed. */
  openFindings: ReviewItem[];
  /** Judged / judgeable, findings included. */
  done: number;
  total: number;
  reviewed: boolean;
}

/**
 * Derives the review model for one product. `fallback` is the run's detection
 * row, used until (or if) the product view has none.
 */
export function buildReviewModel(
  view: ProductAttributesRead | null | undefined,
  fallback: ProductTypeRead,
): ProductReviewModel {
  const detection = view?.productType ?? fallback;
  const latest = latestByKey(view?.reviews);
  const facts = detectionFactItems(detection);
  const shape = listingShapeItem(detection);
  const details = (view?.attributes ?? [])
    .filter((a) => isFoundValue(a.grounding))
    .map((attribute) => ({ item: detailItem(attribute), attribute }));
  const pieces = (view?.components ?? [])
    .filter((a) => isFoundValue(a.grounding))
    .map((attribute) => ({ item: pieceItem(attribute), attribute }));
  const findings = (view?.qcFindings ?? []).map((finding) => ({
    item: findingItem(finding),
    finding,
  }));

  const judgeable = [
    ...facts,
    ...(shape ? [shape] : []),
    ...details.map((d) => d.item),
    ...pieces.map((p) => p.item),
  ];
  const unjudged = judgeable.filter((item) => itemVerdict(latest.get(item.key)) === undefined);
  const openFindings = findings
    .map((f) => f.item)
    .filter((item) => findingVerdict(latest.get(item.key)) === undefined);
  const total = judgeable.length + findings.length;
  const done = total - unjudged.length - openFindings.length;

  return {
    detection,
    facts,
    shape,
    details,
    pieces,
    findings,
    latest,
    unjudged,
    openFindings,
    done,
    total,
    // Without a product view nothing is known, so nothing can be reviewed yet.
    reviewed: Boolean(view) && unjudged.length === 0 && openFindings.length === 0,
  };
}

/** The product-family fact — the item a product-level note rides on. */
export function noteItem(model: ProductReviewModel): ReviewItem | undefined {
  return model.facts.find((f) => f.attributeName === 'productType');
}

/**
 * "Save & next" / "Looks right": every still-unjudged answer becomes CORRECT (a
 * WRONG is never overwritten) and an unsent note rides on the product-family
 * decision — re-stating its current verdict when it was already judged.
 */
export function approvalEvents(
  model: ProductReviewModel,
  context: { productId: string; runId: string },
  note = '',
): ReviewEventCreate[] {
  const events = model.unjudged.map((item) => toReviewEvent(item, 'CORRECT', context));
  const text = note.trim();
  const carrier = noteItem(model);
  if (!text || !carrier) return events;
  const index = events.findIndex((e) => reviewItemKey(e) === carrier.key);
  const existing = events[index];
  if (existing) {
    events[index] = { ...existing, note: text };
  } else {
    const current = model.latest.get(carrier.key);
    events.push(
      toReviewEvent(carrier, current?.verdict === 'WRONG' ? 'WRONG' : 'CORRECT', context, {
        correctedValue: current?.correctedValue ?? '',
        note: text,
      }),
    );
  }
  return events;
}

/** Where one product stands: done, worth a closer look, still to do, or still being read. */
export type ProductReviewStatus = 'reviewed' | 'attention' | 'todo' | 'checking';

/** Detection outcomes that deserve a second look before anything else. */
export function needsAttention(product: ProductTypeRead): boolean {
  return (
    !product.productType ||
    product.productType === 'NO_MATCH' ||
    !['EXACT', 'LADDER'].includes(product.categoryResolution) ||
    (product.confidence > 0 && product.confidence < LOW_CONFIDENCE)
  );
}

/** True when any finding is HIGH or MEDIUM — the ones that make a product "need a look". */
export function hasSeriousFindings(findings: QcFindingRead[] | undefined): boolean {
  return (findings ?? []).some((f) => f.severity === 'HIGH' || f.severity === 'MEDIUM');
}

/**
 * The status dot for a product: `checking` until its product view arrives,
 * then `reviewed` (automatic rule) → `attention` (serious QC findings in this
 * run or the view, or detection problems) → `todo`.
 */
export function productReviewStatus(input: {
  product: ProductTypeRead;
  view: ProductAttributesRead | null | undefined;
  isViewLoading: boolean;
  /** Products with HIGH/MEDIUM findings in this run (from the run's QC read). */
  runAttention?: ReadonlySet<string>;
}): ProductReviewStatus {
  const { product, view, isViewLoading, runAttention } = input;
  if (view === undefined && isViewLoading) return 'checking';
  if (buildReviewModel(view, product).reviewed) return 'reviewed';
  const detection = view?.productType ?? product;
  if (
    needsAttention(detection) ||
    hasSeriousFindings(view?.qcFindings) ||
    Boolean(runAttention?.has(product.productId))
  ) {
    return 'attention';
  }
  return 'todo';
}

/** Dot colour + words for each review status, so the dot never carries meaning alone. */
export const REVIEW_STATUS_META: Record<ProductReviewStatus, { label: string; dot: string }> = {
  reviewed: { label: 'Reviewed', dot: 'bg-success' },
  attention: { label: 'Needs a look', dot: 'bg-warning' },
  todo: { label: 'To do', dot: 'bg-muted-foreground/40' },
  checking: { label: 'Loading…', dot: 'bg-muted-foreground/20 animate-pulse' },
};

/** The QC rules in plain words, from the glossary. */
export const QC_CHECK_LABEL: Record<string, string> = Object.fromEntries(
  Object.entries(QC_CHECK_GLOSSARY).map(([code, e]) => [code, e.label]),
);

/** What a QC rule looks for, in one line; '' for a rule this UI does not know yet. */
export function qcCheckMeaning(checkName: string): string {
  return QC_CHECK_GLOSSARY[checkName]?.meaning ?? '';
}

/** A rule's plain name, falling back to the code in words for a rule this UI does not know yet. */
export function qcCheckLabel(checkName: string): string {
  return QC_CHECK_LABEL[checkName] ?? humanizeEnum(checkName);
}

/** QC severity in words + tone; the order is most severe first. */
export const QC_SEVERITY_META: Record<string, { label: string; tone: StatusTone; rank: number }> = {
  HIGH: { label: QC_SEVERITY_GLOSSARY.HIGH.label, tone: 'error', rank: 0 },
  MEDIUM: { label: QC_SEVERITY_GLOSSARY.MEDIUM.label, tone: 'warning', rank: 1 },
  SUGGESTION: { label: QC_SEVERITY_GLOSSARY.SUGGESTION.label, tone: 'info', rank: 2 },
  INFO: { label: QC_SEVERITY_GLOSSARY.INFO.label, tone: 'muted', rank: 3 },
};

/** Severity meta, lenient for a value outside the vocabulary. */
export function qcSeverityMeta(severity: string): {
  label: string;
  tone: StatusTone;
  rank: number;
} {
  return QC_SEVERITY_META[severity] ?? { label: humanizeEnum(severity), tone: 'muted', rank: 4 };
}

/** The product's title for every surface: the feed title when the catalog was readable. */
export function productTitle(
  product: Pick<ProductTypeRead, 'externalId' | 'productId'>,
  view?: ProductAttributesRead | null,
): string {
  const feedTitle = view?.source?.title.trim() ?? '';
  if (feedTitle !== '') return feedTitle;
  return product.externalId !== '' ? product.externalId : product.productId;
}

/** Leaf of a `A > B > C` category path — "Shoes" from "Apparel > Shoes". */
export function categoryLeaf(path: string): string {
  return path.split('>').pop()?.trim() ?? path;
}

/** "Shoes · Unisex" — what a product is, in words, for list rows. */
export function productInWords(product: ProductTypeRead): string {
  if (!product.productType || product.productType === 'NO_MATCH') return 'No match';
  const what = product.googleProductCategory
    ? categoryLeaf(product.googleProductCategory)
    : humanizeEnum(product.productType);
  return product.personType ? `${what} · ${humanizeEnum(product.personType)}` : what;
}

/** "neck-style" / "NECK_STYLE" → "Neck style", so detail names read as words. */
export function sectionLabel(name: string): string {
  const words = name.replaceAll(/[-_]+/g, ' ').trim();
  if (words === words.toUpperCase()) {
    const lower = words.toLowerCase();
    return lower.charAt(0).toUpperCase() + lower.slice(1);
  }
  return words.charAt(0).toUpperCase() + words.slice(1);
}
